import React from 'react';
import ShowAroundButton from '@/components/help/ShowAroundButton';
import './help-tour.css';
import '@/components/layout/family-page-header.css';

interface PageTourIntroProps {
  /**
   * Screen title block. When provided with `onStartTour`, “Show me around” aligns to the
   * top of this block (My Families / Account pattern).
   */
  heading?: React.ReactNode;
  children: React.ReactNode;
  /**
   * When set, shows “Show me around”. Omit when the heading already includes the tour control
   * (e.g. FamilyPageHeader `onStartTour`).
   */
  onStartTour?: () => void;
  /** Right-aligned actions on the intro/lede row (below Show me around when tour is on the title). */
  trailing?: React.ReactNode;
  className?: string;
}

/**
 * Page lede row for non-family-section screens (My Families, Account, person profile).
 * Family section screens should use FamilyPageHeader instead.
 */
const PageTourIntro: React.FC<PageTourIntroProps> = ({ heading, children, onStartTour, trailing, className = '' }) => {
  const showTour = Boolean(onStartTour);
  const hasTrailing = trailing != null && trailing !== false;

  if (!heading && !showTour) {
    return (
      <div className={`page-tour-intro page-tour-intro--lede-only${className ? ` ${className}` : ''}`}>
        {hasTrailing ? (
          <div className="page-tour-intro__row">
            <div className="page-tour-intro__text page-intro">{children}</div>
            <div className="page-tour-intro__actions">{trailing}</div>
          </div>
        ) : (
          <div className="page-intro">{children}</div>
        )}
      </div>
    );
  }

  return (
    <div
      className={`page-tour-intro${heading ? ' page-tour-intro--with-heading' : ''}${className ? ` ${className}` : ''}`}
    >
      <div className="page-tour-intro__row">
        <div className="page-tour-intro__main">
          {heading ? <div className="page-tour-intro__heading">{heading}</div> : null}
          {hasTrailing ? (
            <div className="page-tour-intro__lede-row">
              <div className="page-tour-intro__text page-intro">{children}</div>
              <div className="page-tour-intro__actions">{trailing}</div>
            </div>
          ) : (
            <div className="page-tour-intro__text page-intro">{children}</div>
          )}
        </div>
        {showTour ? (
          <div className="page-tour-intro__tour" data-tour="show-around">
            <ShowAroundButton onClick={onStartTour!} />
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default PageTourIntro;
